import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_ENDPOINTS } from '../../config/api';
import { logoutUser } from './authSlice';

export const mapIssuedCouponToUi = (coupon) => {
  const issuedDate = coupon.issuedAt ? new Date(coupon.issuedAt) : null;
  const expiredDate = coupon.expiredAt ? new Date(coupon.expiredAt) : null;

  const formatYmd = (date) =>
    !date || Number.isNaN(date.getTime())
      ? null
      : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
          date.getDate()
        ).padStart(2, '0')}`;

  const rawStatus = [coupon.status, coupon.couponStatus, coupon.icStatus].find(
    (v) => typeof v === 'string' && v.trim() !== ''
  );
  const normalizedStatus =
    typeof rawStatus === 'string' ? rawStatus.trim().toLowerCase() : '';

  // status: saved => 保存済み, used => 使用済み
  let isUsed;
  if (normalizedStatus === 'saved') {
    isUsed = '保存済み';
  } else if (normalizedStatus === 'used') {
    isUsed = '使用済み';
  } else if (normalizedStatus === 'expired') {
    isUsed = '期限切れ';
  } else if (normalizedStatus === 'available' || normalizedStatus === 'valid') {
    isUsed = '有効';
  } else if (coupon.isUsed === true) {
    isUsed = '使用済み';
  } else if (coupon.userId != null) {
    isUsed = '保存済み';
  } else {
    isUsed = '有効';
  }

  return {
    id: coupon.cpId ?? coupon.id,
    couponCode: coupon.couponRawCode ?? coupon.couponCode,
    // Prefer label/name; avoid falling back to numeric ctId for UI.
    couponType:
      coupon.couponType ??
      coupon.ctName ??
      coupon.ctCouponTypeName ??
      null,
    couponName:
      coupon.couponName ??
      coupon.ctCouponTypeName ??
      coupon.ctName ??
      null,
    userId: coupon.userId ?? '',
    issuedAt: formatYmd(issuedDate),
    issuedAtRaw: coupon.issuedAt ?? null,
    expiryDate: coupon.expiredAt ? formatYmd(expiredDate) : null,
    isUsed,
    usedAt: coupon.usedAt ?? null,
    managerId: coupon.cmId ?? coupon.managerId ?? null,
    nameStaff: coupon.nameStaff ?? null,
  };
};

const normalizeQrRows = (payload) => {
  let rows = [];
  if (Array.isArray(payload)) rows = payload;
  else if (Array.isArray(payload?.data)) rows = payload.data;
  else if (Array.isArray(payload?.coupons)) rows = payload.coupons;
  else if (payload && typeof payload === 'object') rows = [payload];

  return rows
    .map((r) => {
      const id = r?.cpId ?? r?.id ?? r?.couponId;
      const couponRawCode =
        r?.couponRawCode ?? r?.couponCode ?? r?.coupon_code ?? String(id ?? '');
      const qrBase64 =
        r?.qrBase64 ?? r?.couponQrBase64 ?? r?.coupon_qr_base64 ?? null;
      const couponType = r?.couponType ?? '';
      return { id, couponRawCode, qrBase64, couponType };
    })
    .filter((r) => r.qrBase64);
};

const filterCoupons = (coupons, filters) => {
  let filtered = [...coupons];

  if (filters.couponCode && filters.couponCode.trim() !== '') {
    const searchCode = filters.couponCode.toLowerCase();
    filtered = filtered.filter(
      (coupon) =>
        coupon.managerId != null &&
        String(coupon.managerId).toLowerCase().includes(searchCode)
    );
  }

  if (filters.startDate) {
    const startDate = new Date(filters.startDate);
    filtered = filtered.filter(coupon => {
      const issuedDate = new Date(coupon.issuedAt);
      return issuedDate >= startDate;
    });
  }

  if (filters.endDate) {
    const endDate = new Date(filters.endDate);
    endDate.setHours(23, 59, 59, 999);
    filtered = filtered.filter(coupon => {
      const issuedDate = new Date(coupon.issuedAt);
      return issuedDate <= endDate;
    });
  }

  if (filters.status && filters.status !== 'all') {
    filtered = filtered.filter(coupon => coupon.isUsed === filters.status);
  }

  if (filters.type && filters.type !== 'all') {
    filtered = filtered.filter(coupon => coupon.couponType === filters.type);
  }

  return filtered;
};

// export const fetchAllCoupons = createAsyncThunk(
//   'coupon/fetchAllCoupons',
//   async (_, { rejectWithValue, getState }) => {
//     try {
//       const data = {
//         "message": "Get issued coupons successfully",
//           "data": [
//             {
//               "cpId": 16,
//               "ctId": 2,
//               "cmId": 2,
//               "userId": null,
//               "couponRawCode": "CP-HW4HWZZD5M",
//               "issuedAt": "2026-01-28T07:07:56.000000Z",
//               "expiredAt": "2026-02-08T07:07:56.000000Z",
//               "isUsed": false,
//               "usedAt": null,
//               "couponType": "FX200"
//             },
//             {
//               "cpId": 17,
//               "ctId": 2,
//               "cmId": 2,
//               "userId": 123,
//               "couponRawCode": "CP-UKCUUBCJR7",
//               "issuedAt": "2026-01-28T07:10:00.000000Z",
//               "expiredAt": "2026-02-08T07:10:00.000000Z",
//               "isUsed": true,
//               "usedAt": "2026-01-29T10:00:00.000000Z",
//               "couponType": "FX200"
//             }
//           ]
//         }
//       console.log("test fetchAllCoupons");
//       const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
//       return rows.map(mapIssuedCouponToUi);
//     } catch (error) {
//       return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
//     }
//   }
// );

export const fetchCoupons = createAsyncThunk(
  'coupon/fetchCoupons',
  async (filters, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const token = auth?.token;

      if (!token) {
        return rejectWithValue('認証が必要です');
      }

      const response = await fetch(API_ENDPOINTS.ISSUED_COUPONS_GET_ALL, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data?.message || 'クーポン一覧の取得に失敗しました');
      }

      const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      const mapped = rows.map(mapIssuedCouponToUi);

      const filteredData = filterCoupons(mapped, filters || {});
      return filteredData;
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

export const fetchCouponsQr = createAsyncThunk(
  'coupon/fetchCouponsQr',
  async (ids, { rejectWithValue, getState }) => {
    try {
      if (!ids || !ids.length) {
        return rejectWithValue('クーポンを選択してください');
      }

      const { auth } = getState();
      const token = auth?.token;
      if (!token) {
        return rejectWithValue('認証が必要です');
      }

      const response = await fetch(API_ENDPOINTS.COUPONS_GET_QR, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        const msg = result?.message || 'QR取得に失敗しました';
        return rejectWithValue(msg);
      }

      return normalizeQrRows(result);
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

export const createCoupon = createAsyncThunk(
  'coupon/createCoupon',
  async (couponData, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const token = auth?.token;

      if (!token) {
        return rejectWithValue('認証が必要です');
      }
      if (!API_ENDPOINTS?.ISSUED_COUPONS) {
        return rejectWithValue('ISSUED_COUPONS endpoint is not configured');
      }

      if (couponData.quantity < 1) {
        return rejectWithValue('数量は1以上である必要があります');
      }

      const response = await fetch(API_ENDPOINTS.ISSUED_COUPONS, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ctId: couponData.ctId,
          quantity: couponData.quantity,
          isPrint: couponData.isPrint || false,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data?.message || 'クーポンの作成に失敗しました');
      }

      const coupons = Array.isArray(data?.coupons)
        ? data.coupons
        : Array.isArray(data?.data)
          ? data.data
          : [];

      return {
        ...data,
        coupons,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

export const deleteCouponsBatch = createAsyncThunk(
  'coupon/deleteCouponsBatch',
  async (ids, { rejectWithValue, getState }) => {
    try {
      if (!ids || !ids.length) {
        return rejectWithValue('クーポンを選択してください');
      }

      const { auth } = getState();
      const token = auth?.token;
      if (!token) {
        return rejectWithValue('認証が必要です');
      }

      const response = await fetch(API_ENDPOINTS.COUPONS_DELETE_BATCH, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        const msg = result?.message || '削除に失敗しました';
        return rejectWithValue(msg);
      }

      return ids;
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

const initialState = {
  coupons: [],
  allCoupons: [],
  selectedCoupons: [],
  currentPage: 1,
  filters: {
    couponCode: '',
    startDate: '',
    endDate: '',
    status: 'all',
    type: 'all',
  },
  isLoading: false,
  error: null,
  isCreateModalOpen: false,
};

const couponSlice = createSlice({
  name: 'coupon',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setSelectedCoupons: (state, action) => {
      state.selectedCoupons = action.payload;
    },
    toggleSelectCoupon: (state, action) => {
      const id = action.payload;
      if (state.selectedCoupons.includes(id)) {
        state.selectedCoupons = state.selectedCoupons.filter(item => item !== id);
      } else {
        state.selectedCoupons.push(id);
      }
    },
    selectAllCoupons: (state) => {
      state.selectedCoupons = state.coupons.map(c => c.id);
    },
    deselectAllCoupons: (state) => {
      state.selectedCoupons = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    openCreateModal: (state) => {
      state.isCreateModalOpen = true;
    },
    closeCreateModal: (state) => {
      state.isCreateModalOpen = false;
    },
    upsertCouponsFromSocket: (state, action) => {
      const rows = Array.isArray(action.payload) ? action.payload : [];
      if (!rows.length) return;

      const mapped = rows.map(mapIssuedCouponToUi);
      const byId = new Map(state.coupons.map((c) => [c.id, c]));
      let addedNew = false;
      for (const c of mapped) {
        if (c?.id == null) continue;
        if (!byId.has(c.id)) addedNew = true;
        byId.set(c.id, { ...(byId.get(c.id) || {}), ...c });
      }
      const merged = Array.from(byId.values());
      merged.sort((a, b) => {
        const ta = a?.issuedAtRaw
          ? new Date(a.issuedAtRaw).getTime()
          : a?.issuedAt
            ? new Date(a.issuedAt).getTime()
            : 0;
        const tb = b?.issuedAtRaw
          ? new Date(b.issuedAtRaw).getTime()
          : b?.issuedAt
            ? new Date(b.issuedAt).getTime()
            : 0;
        if (tb !== ta) return tb - ta;
        return (b?.id ?? 0) - (a?.id ?? 0);
      });
      state.coupons = merged;
      if (addedNew) state.currentPage = 1;
    },
    markCouponUsedFromSocket: (state, action) => {
      const { cpId, usedAt, userId } = action.payload || {};
      if (cpId == null) return;
      const id = cpId;

      const patch = (c) => {
        if (!c || c.id !== id) return c;
        return {
          ...c,
          isUsed: '使用済み',
          usedAt: usedAt ?? c.usedAt,
          userId: userId ?? c.userId,
        };
      };

      state.coupons = state.coupons.map(patch);
      state.allCoupons = state.allCoupons.map(patch);

      // If selected coupon became used, de-select it
      state.selectedCoupons = state.selectedCoupons.filter((x) => x !== id);
    },
    removeCouponsFromSocket: (state, action) => {
      const ids = Array.isArray(action.payload?.ids) ? action.payload.ids : [];
      if (!ids.length) return;
      const idSet = new Set(ids);
      state.coupons = state.coupons.filter((c) => !idSet.has(c.id));
      state.allCoupons = state.allCoupons.filter((c) => !idSet.has(c.id));
      state.selectedCoupons = state.selectedCoupons.filter((x) => !idSet.has(x));
    },
  },
  extraReducers: (builder) => {
    builder
      // .addCase(fetchAllCoupons.fulfilled, (state, action) => {
      //   state.allCoupons = action.payload;
      // })
      .addCase(fetchCoupons.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCoupons.fulfilled, (state, action) => {
        state.isLoading = false;
        state.coupons = action.payload;
        state.selectedCoupons = [];
        state.currentPage = 1;
      })
      .addCase(fetchCoupons.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createCoupon.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createCoupon.fulfilled, (state) => {
        state.isLoading = false;
        state.isCreateModalOpen = false;
      })
      .addCase(createCoupon.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteCouponsBatch.fulfilled, (state, action) => {
        const deletedIds = action.payload;
        const idSet = new Set(deletedIds);
        state.coupons = state.coupons.filter((c) => !idSet.has(c.id));
        state.allCoupons = state.allCoupons.filter((c) => !idSet.has(c.id));
        state.selectedCoupons = [];
      })
      .addCase(logoutUser.fulfilled, () => ({ ...initialState }));
  },
});

export const {
  setFilters,
  setCurrentPage,
  setSelectedCoupons,
  toggleSelectCoupon,
  selectAllCoupons,
  deselectAllCoupons,
  clearError,
  openCreateModal,
  closeCreateModal,
  upsertCouponsFromSocket,
  markCouponUsedFromSocket,
  removeCouponsFromSocket,
} = couponSlice.actions;

export default couponSlice.reducer;
